// SCR-STAFF-932 (بخش انواع دستگاه — AP-ORG-02؛ US-ORG-031): انواع دستگاه با واحد شمارنده (Shot، Pulse، Minute، Session، None)، کد مجموعه‌پارامتر CFG
// و برچسب‌ها؛ انواع بسته تخصصی با منشأ؛ حذف نوع در حال استفاده رد می‌شود (BR-ORG-032).
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import { unwrap, type CounterUnit, type DeviceTypeView } from '@phoenix/api-client';
import { useIdempotencyKey, useTenantSession, useWriteAccess } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import {
  applyServerErrors,
  CheckboxRHF,
  ConfirmDialog,
  DataTable,
  faDigits,
  FormDialog,
  ListPage,
  SelectRHF,
  TextFieldRHF,
  zRequiredText,
  type DataTableColumn,
} from '@phoenix/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { WriteNotice } from '../settings/common';
import { TAG_CODE_PATTERN, useResourceTags, useTagLabel } from './common';
import { COUNTER_UNITS, deviceKeys, useDeviceTypes } from './deviceCommon';

export const SCR_STAFF_932_TYPES = 'SCR-STAFF-932';

export function DeviceTypesPage() {
  const { t } = useTranslation('org');
  const { tenantApi, session } = useTenantSession();
  const write = useWriteAccess('org.device.manage');
  const types = useDeviceTypes();
  const tagLabel = useTagLabel();
  const tags = useResourceTags();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<DeviceTypeView | null | undefined>(undefined);
  const [deleting, setDeleting] = useState<DeviceTypeView | null>(null);

  const remove = useMutation({
    mutationFn: (type: DeviceTypeView) =>
      unwrap(tenantApi.DELETE('/api/v1/organization/device-types/{id}', { params: { path: { id: type.id } } })),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: deviceKeys.types(session?.tenantId) });
      setDeleting(null);
    },
  });

  const columns: DataTableColumn<DeviceTypeView>[] = [
    { id: 'code', headerKey: 'org:device_types.columns.code', cell: (row) => <bdi dir="ltr">{row.code}</bdi> },
    { id: 'name', headerKey: 'org:device_types.columns.name', cell: (row) => row.name },
    { id: 'unit', headerKey: 'org:device_types.columns.unit', cell: (row) => t(`org:counter_unit.${row.counterUnit}`) },
    {
      id: 'tags',
      headerKey: 'org:device_types.columns.tags',
      cell: (row) => (
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
          {row.tagCodes.map((code) => (
            <Chip
              key={code}
              size="small"
              variant="outlined"
              label={tagLabel(
                tags.data?.items.find((tag) => tag.code === code),
                code,
              )}
            />
          ))}
          {row.sourcePackCode && <Chip size="small" color="secondary" label={t('org:device_types.from_pack')} />}
        </Stack>
      ),
    },
    { id: 'devices', headerKey: 'org:device_types.columns.devices', cell: (row) => faDigits(Number(row.deviceCount)) },
    {
      id: 'actions',
      headerKey: 'org:device_types.columns.actions',
      cell: (row) => (
        <Stack direction="row" spacing={0.5}>
          <Tooltip title={t('org:device_types.edit')}>
            <span>
              <IconButton aria-label={`${t('org:device_types.edit')} ${row.name}`} onClick={() => setEditing(row)} disabled={!write.canWrite} data-testid={`device-type-edit-${row.code}`}>
                <EditOutlinedIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={t('org:device_types.delete')}>
            <span>
              <IconButton
                aria-label={`${t('org:device_types.delete')} ${row.name}`}
                onClick={() => setDeleting(row)}
                disabled={!write.canWrite || Number(row.deviceCount) > 0}
                data-testid={`device-type-delete-${row.code}`}
              >
                <DeleteOutlineIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      ),
    },
  ];

  return (
    <ListPage
      screenId={SCR_STAFF_932_TYPES}
      titleKey="org:device_types.title"
      subtitleKey="org:device_types.subtitle"
      actions={
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setEditing(null)} disabled={!write.canWrite} data-testid="device-type-create">
          {t('org:device_types.add')}
        </Button>
      }
      filters={<WriteNotice write={write} />}
    >
      <DataTable
        captionKey="org:device_types.caption"
        columns={columns}
        rows={types.data?.items}
        getRowId={(row) => row.id}
        isLoading={types.isPending}
        error={types.error}
        onRetry={() => void types.refetch()}
        emptyTitleKey="org:device_types.empty"
      />
      {editing !== undefined && <DeviceTypeDialog type={editing} onClose={() => setEditing(undefined)} />}
      <ConfirmDialog
        open={deleting !== null}
        titleKey="org:device_types.delete_title"
        messageKey="org:device_types.delete_message"
        messageValues={{ name: deleting?.name ?? '' }}
        confirmKey="common:actions.delete"
        destructive
        busy={remove.isPending}
        onConfirm={() => deleting && remove.mutate(deleting)}
        onCancel={() => setDeleting(null)}
        error={remove.error ?? undefined}
      />
    </ListPage>
  );
}

const typeSchema = z.object({
  code: z.string(),
  name: zRequiredText(200),
  counterUnit: z.enum(COUNTER_UNITS as [CounterUnit, ...CounterUnit[]], { error: 'common:validation.required' }),
  parameterSetCode: z.string().max(64, { error: 'common:validation.too_long' }),
  tagCodes: z.array(z.string()),
  isActive: z.boolean(),
});
type TypeForm = z.input<typeof typeSchema>;

function DeviceTypeDialog({ type, onClose }: { type: DeviceTypeView | null; onClose: () => void }) {
  const { t } = useTranslation('org');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const idempotency = useIdempotencyKey();
  const tags = useResourceTags();
  const tagLabel = useTagLabel();
  const [submitError, setSubmitError] = useState<unknown>();
  const creating = type === null;
  const schema = useMemo(
    () =>
      typeSchema.superRefine((value, ctx) => {
        if (creating && !TAG_CODE_PATTERN.test(value.code.trim())) {
          ctx.addIssue({ code: 'custom', path: ['code'], message: 'errors:validation.org_tag_code' });
        }
      }),
    [creating],
  );
  const { control, handleSubmit, formState, setError } = useForm<TypeForm>({
    resolver: zodResolver(schema),
    defaultValues: {
      code: type?.code ?? '',
      name: type?.name ?? '',
      counterUnit: type?.counterUnit ?? 'Shot',
      parameterSetCode: type?.parameterSetCode ?? '',
      tagCodes: [...(type?.tagCodes ?? [])],
      isActive: type?.isActive ?? true,
    },
  });

  const save = useMutation({
    mutationFn: (form: TypeForm) => {
      const common = {
        name: form.name,
        counterUnit: form.counterUnit as CounterUnit,
        parameterSetCode: form.parameterSetCode.trim() || null,
        tagCodes: form.tagCodes,
      };
      return creating
        ? unwrap(
            tenantApi.POST('/api/v1/organization/device-types', {
              body: { ...common, code: form.code.trim() },
              headers: { 'Idempotency-Key': idempotency.current() },
            }),
          )
        : unwrap(
            tenantApi.PUT('/api/v1/organization/device-types/{id}', {
              params: { path: { id: type.id } },
              body: { ...common, isActive: form.isActive, rowVersion: type.rowVersion },
            }),
          );
    },
    onMutate: () => setSubmitError(undefined),
    onSettled: (_data, error) => {
      if (creating) {
        idempotency.settle(error ?? undefined);
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: deviceKeys.types(session?.tenantId) });
      onClose();
    },
    onError: (error) => {
      const applied = applyServerErrors(error, setError, {
        fields: ['code', 'name', 'counterUnit', 'parameterSetCode', 'tagCodes'],
        aliases: { counter_unit: 'counterUnit', parameter_set_code: 'parameterSetCode', tag_codes: 'tagCodes' },
        codeFields: { 'PHX-ORG-0307': 'code', 'PHX-ORG-0405': 'tagCodes' },
      });
      setSubmitError(applied > 0 ? undefined : error);
    },
  });

  return (
    <FormDialog
      open
      screenId="SCR-STAFF-932-TYPE-FORM"
      titleKey={creating ? 'org:device_types.create_title' : 'org:device_types.edit_title'}
      onSubmit={() => void handleSubmit((form) => save.mutate(form))()}
      onClose={onClose}
      isDirty={formState.isDirty}
      isSubmitting={save.isPending}
      submitError={submitError}
    >
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextFieldRHF
          control={control}
          name="code"
          labelKey="org:device_types.form.code"
          helperKey={creating ? 'org:device_types.form.code_help' : 'org:device_types.form.code_immutable'}
          required
          fullWidth
          disabled={!creating}
          slotProps={{ htmlInput: { dir: 'ltr', 'data-testid': 'device-type-code' } }}
        />
        <TextFieldRHF control={control} name="name" labelKey="org:device_types.form.name" required fullWidth autoFocus slotProps={{ htmlInput: { 'data-testid': 'device-type-name' } }} />
      </Stack>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <SelectRHF
          control={control}
          name="counterUnit"
          labelKey="org:device_types.form.counter_unit"
          required
          fullWidth
          options={COUNTER_UNITS.map((value) => ({ value, labelKey: `org:counter_unit.${value}` }))}
        />
        <TextFieldRHF
          control={control}
          name="parameterSetCode"
          labelKey="org:device_types.form.parameter_set"
          helperKey="org:device_types.form.parameter_set_help"
          fullWidth
          slotProps={{ htmlInput: { dir: 'ltr', 'data-testid': 'device-type-parameter-set' } }}
        />
      </Stack>
      <Controller
        control={control}
        name="tagCodes"
        render={({ field }) => (
          <TextField
            select
            fullWidth
            label={t('org:device_types.form.tags')}
            name={field.name}
            inputRef={field.ref}
            value={field.value}
            onBlur={field.onBlur}
            onChange={(event) => field.onChange(typeof event.target.value === 'string' ? event.target.value.split(',') : event.target.value)}
            slotProps={{ select: { multiple: true }, htmlInput: { 'data-testid': 'device-type-tags' } }}
          >
            {(tags.data?.items ?? []).map((tag) => (
              <MenuItem key={tag.code} value={tag.code}>
                {tagLabel(tag)}
              </MenuItem>
            ))}
          </TextField>
        )}
      />
      {!creating && <CheckboxRHF control={control} name="isActive" labelKey="org:device_types.form.is_active" />}
    </FormDialog>
  );
}
